import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { sessionManager } from '../../config/sessionManager';

// Zod validation schema for client account
const accountSchema = z.object({
  name: z.string().min(1, 'Organization name is required').trim(),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
});

type AccountFormData = z.infer<typeof accountSchema>;

const Account: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [clientData, setClientData] = useState<any>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<AccountFormData>({
    resolver: zodResolver(accountSchema),
    defaultValues: {
      name: '',
      address: '',
      city: '',
      state: '',
      zip: '',
    },
  });

  const loadClientData = useCallback(async () => {
    try {
      setIsLoading(true);
      const clientId = sessionManager.getClientId();

      if (!clientId) {
        showErrorMsg('Client information not found. Please contact support.');
        return;
      }

      const response = await api.get(API_URLS.GET_CLIENT_BY_ID, {
        params: { clientId },
      });

      if (response.data.success) {
        const client = response.data.data;
        setClientData(client);

        // Pre-fill form with client data
        reset({
          name: client.name || '',
          address: client.address || '',
          city: client.city || '',
          state: client.state || '',
          zip: client.zip || '',
        });

        // Set logo preview if exists
        if (client.image) {
          setSelectedImage(`${process.env.REACT_APP_UPLOAD_URL}uploads/clients/${client.image}`);
        }
      }
    } catch (error) {
      console.error('Error loading client data:', error);
      showErrorMsg('Failed to load account information.');
    } finally {
      setIsLoading(false);
    }
  }, [reset]);

  // Load client data on component mount
  useEffect(() => {
    loadClientData();
  }, [loadClientData]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        showErrorMsg('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showErrorMsg('Image size must be less than 5MB');
        return;
      }

      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setSelectedImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data: AccountFormData) => {
    try {
      setIsLoading(true);

      if (!clientData?._id) {
        showErrorMsg('Client ID not found');
        return;
      }

      let response;
      const updateData = {
        _id: clientData._id,
        ...data,
      };

      // If a new logo is selected, handle file upload with FormData
      if (logoFile) {
        const submitData = new FormData();
        submitData.append('image', logoFile);
        submitData.append('_id', clientData._id);

        // Append other fields
        Object.entries(updateData).forEach(([key, value]) => {
          if (key !== '_id') {
            submitData.append(key, String(value));
          }
        });

        response = await api.put(API_URLS.UPDATE_CLIENT, submitData);
      } else {
        response = await api.put(API_URLS.UPDATE_CLIENT, updateData);
      }

      if (response.data.success) {
        showSuccessMsg('Account information updated successfully!');

        // Update session client name if changed
        if (data.name !== clientData.name) {
          sessionManager.setClientName(data.name);
        }

        // Reload client data to get updated information
        loadClientData();

        // Clear logo file selection
        setLogoFile(null);
      } else {
        showErrorMsg(response.data.message || 'Failed to update account information');
      }
    } catch (error: any) {
      console.error('Error updating account:', error);
      if (error.response?.data?.message) {
        showErrorMsg(error.response.data.message);
      } else {
        showErrorMsg('Failed to update account information');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Check if user has permission to edit client info
  const hasEditPermission = () => {
    const clientId = sessionManager.getClientId();
    const isAdmin = sessionManager.isAdmin();
    const isEmployee = sessionManager.isEmployee();

    // Head coaches and assistant coaches always have permission
    if (sessionManager.isHeadCoach() || sessionManager.isAssistantCoach()) {
      return true;
    }

    // Employees have access to view/edit organization info (they can access all client accounts)
    if (isEmployee) {
      return true;
    }

    // Admins only have permission if they have a clientId (not employees)
    if (isAdmin && clientId) {
      return true;
    }

    return false;
  };

  if (!hasEditPermission()) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-pink-50 flex items-center justify-center">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white shadow-xl rounded-2xl p-8 border border-red-100 text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
              <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.35 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h3>
            <p className="text-gray-600 mb-6">
              Only organization owners (Head Coaches, Assistant Coaches) and STATDOG employees can access account
              information.
            </p>
            <button
              onClick={() => window.history.back()}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading && !clientData) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center">
        <div className="relative">
          <div
            className="rounded-full h-16 w-16 border-4 border-transparent border-r-emerald-400 animate-spin"
            style={{
              animationDirection: 'reverse',
              animationDuration: '1.5s',
            }}
          ></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-green-50 via-white to-emerald-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-700 text-white">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
            </div>
            <div>
              <h1 className="text-xl font-bold">Organization Settings</h1>
              <p className="text-green-100 text-base mt-1">Manage your organization's information and settings</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white max-w-5xl shadow-xl mx-auto rounded-2xl overflow-hidden border border-gray-100">
          <form onSubmit={handleSubmit(onSubmit)} className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
              {/* Organization Information */}
              <div className="h-full">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100 h-full flex flex-col">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Team Information</h3>
                  </div>

                  <div className="space-y-6 flex-1">
                    <div className="group">
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                        <span>Organization Name *</span>
                      </label>
                      <input
                        type="text"
                        {...register('name')}
                        className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-green-500/20 group-hover:border-green-300 ${
                          errors.name ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-green-500'
                        }`}
                        placeholder="Enter organization name"
                        disabled={isLoading || isSubmitting}
                      />
                      {errors.name && (
                        <p className="text-red-500 text-sm mt-2 flex items-center space-x-1">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span>{errors.name.message}</span>
                        </p>
                      )}
                    </div>

                    <div className="group">
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        <span>Address</span>
                      </label>
                      <input
                        type="text"
                        {...register('address')}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-green-500/20 group-hover:border-green-300 focus:border-green-500"
                        placeholder="Enter street address"
                        disabled={isLoading || isSubmitting}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="group">
                        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l7-3 7 3z"
                            />
                          </svg>
                          <span>City</span>
                        </label>
                        <input
                          type="text"
                          {...register('city')}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-green-500/20 group-hover:border-green-300 focus:border-green-500"
                          placeholder="City"
                          disabled={isLoading || isSubmitting}
                        />
                      </div>

                      <div className="group">
                        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                            />
                          </svg>
                          <span>State</span>
                        </label>
                        <input
                          type="text"
                          {...register('state')}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-green-500/20 group-hover:border-green-300 focus:border-green-500"
                          placeholder="State"
                          disabled={isLoading || isSubmitting}
                        />
                      </div>

                      <div className="group">
                        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                            />
                          </svg>
                          <span>ZIP Code</span>
                        </label>
                        <input
                          type="text"
                          {...register('zip')}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-green-500/20 group-hover:border-green-300 focus:border-green-500"
                          placeholder="ZIP"
                          disabled={isLoading || isSubmitting}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Logo Upload */}
              <div className="h-full">
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-100 h-full flex flex-col">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Organization Logo</h3>
                  </div>

                  <div className="space-y-6 flex-1">
                    {/* Logo Preview */}
                    <div className="flex items-center justify-center w-full">
                      <div className="w-40 h-40 border-2 border-dashed border-emerald-300 rounded-2xl flex items-center justify-center bg-white/50 backdrop-blur-sm hover:border-emerald-400 transition-colors duration-200">
                        {selectedImage ? (
                          <img
                            src={selectedImage}
                            alt="Organization logo preview"
                            className="w-full h-full object-contain rounded-2xl"
                          />
                        ) : (
                          <div className="text-center">
                            <svg
                              className="mx-auto h-16 w-16 text-emerald-400"
                              stroke="currentColor"
                              fill="none"
                              viewBox="0 0 48 48"
                            >
                              <path
                                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <p className="mt-3 text-sm font-medium text-gray-600">No logo uploaded</p>
                            <p className="text-xs text-gray-500">Click below to upload</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* File Input */}
                    <div className="group">
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        <span>Upload New Logo</span>
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-emerald-500/20 group-hover:border-emerald-300 focus:border-emerald-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                        disabled={isLoading || isSubmitting}
                      />
                      <p className="text-xs text-gray-500 mt-2 flex items-center space-x-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>Supported formats: JPEG, PNG, GIF, WebP (max 5MB)</span>
                      </p>
                    </div>

                    {logoFile && (
                      <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <svg className="h-4 w-4 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-emerald-800">{logoFile.name}</p>
                            <p className="text-xs text-emerald-600">Ready to upload</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                  <span>Your organization data is secure and encrypted</span>
                </div>
                <button
                  type="submit"
                  disabled={isLoading || isSubmitting}
                  className="group relative bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-3 rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-4 focus:ring-green-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  {isLoading || isSubmitting ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Saving Changes...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Update Organization</span>
                    </div>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Account;
